import { useEffect, useState } from 'react';
import { LeavePageModal } from 'shared/ui/ModalDialog';

import { StepService } from './StepService';
import { ProgressBar, type ProgressBarItem } from './ProgressBar';
import { StepType } from './StepType';
import { StepValidation } from './StepValidation';
import { StepSchedule } from './StepSchedule';
import { StepBid } from './StepBid';
import { StepBudget } from './StepBudget';
import { StepName } from './StepName';
import { StepTargetArea } from './StepTargetArea';
import { useCampaignType, useCreateCampaignStoreActions, usePreSelectedType } from './CreateCampaignStoreHook';

interface CreateCampaignWizardProps {
  onClose: () => void;
}

export function CreateCampaignWizard(props: CreateCampaignWizardProps) {
  const { onClose } = props;
  const preselectedCampaignType = usePreSelectedType();
  const campaignType = useCampaignType();
  const { resetStore } = useCreateCampaignStoreActions();
  const [currentStep, setCurrentStep] = useState(1);
  const isSalesCampaignTypePreselected = preselectedCampaignType === 'sales';
  const isAppointmentCampaignTypePreselected = preselectedCampaignType === 'appt';
  const isSalesCampaignType = campaignType === 'sales';
  const isAppointmentType = campaignType === 'appt';

  useEffect(() => {
    return () => resetStore();
  }, [resetStore]);

  const getProgressBarItems = (): ProgressBarItem[] => {
    const items = [
      { text: '1', title: 'Name', id: '1' },
      { text: '2', title: 'Service', id: '2' },
      { text: '3', title: 'Type', id: '3' },
      { text: '4', title: 'Target Area', id: '4' },
      { text: '5', title: 'Validation', id: '5' },
    ];

    if (!isAppointmentCampaignTypePreselected) {
      items.push({ text: '6', title: 'Schedule', id: '6' });
    }

    if (!isSalesCampaignTypePreselected) {
      items.push(
        { text: (items.length + 1).toString(), title: 'Bid', id: (items.length + 1).toString() },
        { text: (items.length + 2).toString(), title: 'Budget', id: (items.length + 2).toString() }
      );
    }

    return items;
  };

  const getSteps = (): { component: JSX.Element; id: string }[] => {
    const steps = [
      {
        component: (
          <StepName
            onCancel={onClose}
            onNext={goNextStep}
          />
        ),
        id: '1',
      },
      {
        component: (
          <StepService
            onBack={goPrevStep}
            onNext={goNextStep}
          />
        ),
        id: '2',
      },
      {
        component: (
          <StepType
            onBack={goPrevStep}
            onNext={goNextStep}
          />
        ),
        id: '3',
      },
      {
        component: (
          <StepTargetArea
            onBack={goPrevStep}
            onNext={goNextStep}
          />
        ),
        id: '4',
      },
      {
        component: (
          <StepValidation
            onBack={goPrevStep}
            onNext={goNextStep}
          />
        ),
        id: '5',
      },
    ];

    if (!isAppointmentType) {
      steps.push({
        component: (
          <StepSchedule
            onBack={goPrevStep}
            onNext={goNextStep}
            onCreate={onClose}
          />
        ),
        id: '6',
      });
    }

    if (!isSalesCampaignType) {
      steps.push(
        {
          component: (
            <StepBid
              onBack={goPrevStep}
              onNext={goNextStep}
            />
          ),
          id: (steps.length + 1).toString(),
        },
        {
          component: (
            <StepBudget
              onBack={goPrevStep}
              onCreate={onClose}
            />
          ),
          id: (steps.length + 1).toString(),
        }
      );
    }

    return steps;
  };

  const goPrevStep = () => {
    setCurrentStep((currentStep) => currentStep - 1);
  };

  const goNextStep = () => {
    setCurrentStep((currentStep) => currentStep + 1);
  };

  return (
    <div className="mt-3 flex flex-col items-center">
      <ProgressBar
        stepNumber={currentStep}
        items={getProgressBarItems()}
        className="mb-19"
      />
      <div className="w-138">{getSteps()[currentStep - 1].component}</div>
      <LeavePageModal
        isOpen
        onConfirm={onClose}
      />
    </div>
  );
}
